/**
 * The slice of the Web Speech API this app actually uses.
 *
 * TypeScript's DOM lib does not declare speech recognition, and the browsers
 * that ship it disagree on the constructor's name. Declaring only what is used
 * keeps the app from pretending to more of the spec than it relies on, and
 * keeps the declaration in one place instead of once per dictation surface.
 */

export type SpeechRecognitionAlternativeLike = { transcript: string };

export type SpeechRecognitionResultLike = {
    isFinal: boolean;
    0: SpeechRecognitionAlternativeLike;
};

export type SpeechRecognitionEventLike = {
    /** The first result this event changed. Earlier results are unchanged. */
    resultIndex: number;
    results: { length: number;[index: number]: SpeechRecognitionResultLike };
};

export type SpeechRecognitionErrorEventLike = { error: string };

export type SpeechRecognitionLike = {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    start: () => void;
    stop: () => void;
    abort: () => void;
    onstart: (() => void) | null;
    onresult: ((event: SpeechRecognitionEventLike) => void) | null;
    onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
    onend: (() => void) | null;
};

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

/** The browser's recogniser, or null where there is none (Firefox). */
export function speechRecognitionConstructor(): SpeechRecognitionConstructor | null {
    const browser = window as unknown as {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };
    return browser.SpeechRecognition ?? browser.webkitSpeechRecognition ?? null;
}
